import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import {
  InputComponent,
  ISearchOptions,
  SharedConstants,
  SharedCssClassNames,
  SharedValidationPatterns
} from '@app/eds';

export type searchInputTextType = {
  searchBy: string;
  text: string;
};

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit {
  // Child componenet, used to access its method
  @ViewChild('appInput') inputComponent!: InputComponent;

  // Placeholder text on the search box to put on
  @Input() placeholder!: string;

  // width of dropdown option in search
  @Input() dropDownWidth!: string;

  // Disables the whole search box i.e dropdown and inputbox
  @Input() isSearchBoxDisabled = false;

  // Input text entered by the user
  inputText: string = SharedConstants.EMPTY_STRING;

  // Whether to hide Search button or not
  @Input() hideButton = false;

  // Whether to apply focus on search box or not
  @Input() showFocus = false;

  // Whether to focus on input component or not
  inputFocused = false;

  // Data to show on the dropdown select options
  @Input() searchOptions: ISearchOptions[] = [];

  // Default value of the dropdown
  @Input() defaultSelectValue!: ISearchOptions;

  // Whether to disable search button or not
  @Input() disableButton = false;

  // Search key of the dropdown value
  @Input() searchKey: string = SharedConstants.EMPTY_STRING;

  // Tells if error exists or not.
  @Input() hasCustomError = false;

  // Error Message to be displayed.
  @Input() customErrorMsg: string = SharedConstants.EMPTY_STRING;

  // To Show Spinner inplace of search btn.
  @Input() showSpinner = false;

  // TO disable close icon in input field.
  @Input() disableClose = false;

  // This function will emit the event.target.value on paste.
  @Output() inputPasteEmiter: EventEmitter<any> = new EventEmitter<any>();

  // Emits event when the search button pressed/enter key pressed
  @Output() inputEnterClickHandler: EventEmitter<searchInputTextType> =
    new EventEmitter<searchInputTextType>();

  // Emits the event when in change in the dropdown value
  @Output() dropDownSelectionChange: EventEmitter<string> = new EventEmitter<string>();

  // Emits event cross icon is clicked on search box
  @Output() cancelSearchHandler: EventEmitter<null> = new EventEmitter<null>();

  // Emits event when a key is pressed
  @Output() keyUpHandler: EventEmitter<{ text: string }> = new EventEmitter<{
    text: string;
  }>();

  // For reset timer when user typing
  private _keyPressTimer!: number | ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.searchKey = this.searchOptions[0]?.name;
  }

  /**
   * Handler when submit button is clicked
   */
  onClick = () =>
    this.inputEnterClickHandler.emit({ searchBy: this.searchKey, text: this.inputText });

  /**
   * When user inputs any word/text and presses enter in keyboard
   */
  onInputEnter = (value: string) => {
    this.inputText = value;
    this.inputEnterClickHandler.emit({ searchBy: this.searchKey, text: value });
  };

  /**
   * When a text is entered in the input box
   *
   * @param {string} val entered text
   */
  onInputChangeHandler = (val: string) => (this.inputText = val);

  /**
   * When a new search by value is selected from dropdown
   *
   * @param {string} selKey key of that field selected
   */
  onSelectionChange = (selKey: string) => {
    this.searchKey = selKey;
    this.dropDownSelectionChange.emit(selKey);
  };

  /**
   * Will fire when a key is pressed
   *
   * @param {string} val entered text
   */
  onKeyUp = (val: string) => {
    const selected = this.searchOptions.find((it) => it.name === this.searchKey);
    const trimmedVal = val.trim();

    if (selected && selected.customizeValidationSearchInput) {
      clearTimeout(this._keyPressTimer as number); //clear any running timeout on key up
      this._keyPressTimer = setTimeout(() => {
        const _value =
          (selected.customizeValidationSearchInput &&
            selected.customizeValidationSearchInput(val)) ||
          trimmedVal;
        this.setInputField(_value);
        // change the value in the input should emit the event
        this.keyUpHandler.emit({ text: _value });
      }, 1000);
      return;
    }

    // REMOVE SPECIAL CHAR WITH EMPTY SPACE.
    if (trimmedVal.match(SharedValidationPatterns.ALPHA_NUMERIC_PATTERN_REGEX)) {
      const _value = this.inputComponent?.inputText.value.replace(
        SharedValidationPatterns.ALPHA_NUMERIC_PATTERN_REGEX,
        SharedConstants.EMPTY_STRING
      );

      this.setInputField(_value);

      // change the value in input should send the value change
      this.keyUpHandler.emit({ text: _value });
      return;
    }

    if (!trimmedVal.length) {
      this.hasCustomError = false;
    }

    this.keyUpHandler.emit({ text: val });
  };

  /**
   * This function will trigger when something is changed in input
   *
   * @param event
   */
  onPaste(event: any) {
    this.inputPasteEmiter.emit(event);
  }

  /**
   * When close icon is clicked in the search box
   */
  onCancel = () => {
    if (!this.disableClose) {
      this.inputText = SharedConstants.EMPTY_STRING;
      this.cancelSearchHandler.emit();
    }
  };

  /**
   * Clears input field.
   */
  clearInputField = () => this.inputComponent?.clearInput();

  /**
   * Calls the setInputValue() method of child component i.e input
   * to set the input box value.
   *
   * @param {string} fieldValue
   */
  setInputField = (fieldValue: string) => this.inputComponent?.setInputValue(fieldValue);

  /**
   * get placeholder text , if not provided then return common placeholder
   *
   * @returns {string} placeholder text
   */
  getPlaceholderText = () =>
    !this.placeholder ? 'common.placeholder.search' : this.placeholder;

  /**
   * When input is on focused , used this to handle bottom blur border
   */
  onFocus = () => (this.inputFocused = true);

  /**
   * When input is on blured , used this to handle bottom blur border
   */
  onBlur = () => (this.inputFocused = false);

  /**
   * Clear input when called.
   */
  onClearInput = () => this.inputComponent?.onCancel();

  /**
   * When input needs to show border bottom
   *
   * @returns {string} border class name
   */
  getBorderClass = () => {
    if (this.hasCustomError) {
      return SharedCssClassNames.ERROR;
    }

    if (this.showFocus) {
      return this.inputFocused ? SharedCssClassNames.FOCUS : SharedCssClassNames.UNFOCUS;
    } else {
      return SharedConstants.EMPTY_STRING;
    }
  };

  /**
   * Condition to display select component inside search.
   *
   * @returns {boolean} bool
   */
  displaySelect = () => this.searchOptions && this.searchOptions.length;
}
